import type {
  BillingStatusResponse,
  CancelBillingSubscriptionInput,
  ConfirmBillingCheckoutInput,
  CreateBillingCheckoutResponse,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

export const billingStatusKey = ['billing', 'status'] as const;

export function useBillingStatus() {
  return useQuery({
    queryKey: billingStatusKey,
    queryFn: () => api<BillingStatusResponse>('/billing/status'),
    staleTime: 30_000,
    // Mobile UPI apps can return control before Razorpay delivers its success
    // webhooks. Keep the paywall fresh until the provider leaves `created` so
    // a paid user is not stranded waiting for a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.subscription?.providerStatus === 'created' ? 5_000 : false,
  });
}

export function useCreateBillingCheckout() {
  return useMutation({
    mutationFn: () => api<CreateBillingCheckoutResponse>('/billing/checkout', { method: 'POST' }),
  });
}

export function useConfirmBillingCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmBillingCheckoutInput) =>
      api<BillingStatusResponse>('/billing/confirm', { method: 'POST', json: input }),
    onSuccess: (status) => queryClient.setQueryData(billingStatusKey, status),
  });
}

export function useCancelBillingSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelBillingSubscriptionInput) =>
      api<BillingStatusResponse>('/billing/cancel', { method: 'POST', json: input }),
    onSuccess: (status) => queryClient.setQueryData(billingStatusKey, status),
  });
}
